import React, { createContext, useContext, useState, useEffect } from 'react';
import { vehicleService } from '../services/vehicleService';
import { inquiryService } from '../services/inquiryService';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [vehicles, setVehicles] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  // CRITICAL: Start with loading as true
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetching both in parallel to save time
      const [vehiclesData, inquiriesData] = await Promise.all([
        vehicleService.getVehicles(),
        inquiryService.getInquiries()
      ]);
      setVehicles(vehiclesData || []);
      setInquiries(inquiriesData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      // Delay slightly to ensure smooth transition
      setLoading(false);
    }
  };

  const addVehicle = async (vehicle) => {
    try {
      const newVehicle = await vehicleService.createVehicle(vehicle);
      setVehicles(prev => [newVehicle, ...prev]);
      return newVehicle;
    } catch (error) {
      console.error('Error adding vehicle:', error);
      throw error;
    }
  };

  const updateVehicle = async (id, updatedVehicle) => {
    try {
      const vehicle = await vehicleService.updateVehicle(id, updatedVehicle);
      setVehicles(prev => prev.map(v => v.id === parseInt(id) ? vehicle : v));
      return vehicle;
    } catch (error) {
      console.error('Error updating vehicle:', error);
      throw error;
    }
  };

  const deleteVehicle = async (id) => {
    try {
      await vehicleService.deleteVehicle(id);
      setVehicles(prev => prev.filter(v => v.id !== id));
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      throw error;
    }
  };

  const toggleVehicleStatus = async (id) => {
    try {
      const vehicle = vehicles.find(v => v.id === id);
      if (!vehicle) return;
      
      const updatedVehicle = await vehicleService.toggleVehicleStatus(id, vehicle.status);
      setVehicles(prev => prev.map(v => v.id === id ? updatedVehicle : v));
    } catch (error) {
      console.error('Error toggling vehicle status:', error);
      throw error;
    }
  };

  const addInquiry = async (inquiry) => {
    try {
      const newInquiry = await inquiryService.createInquiry(inquiry);
      setInquiries(prev => [newInquiry, ...prev]);
      return newInquiry;
    } catch (error) {
      console.error('Error adding inquiry:', error);
      throw error;
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      const inquiry = await inquiryService.updateInquiryStatus(id, status);
      setInquiries(prev => prev.map(i => i.id === id ? inquiry : i));
      return inquiry;
    } catch (error) {
      console.error('Error updating inquiry status:', error);
      throw error;
    }
  };

  const value = {
    vehicles,
    inquiries,
    loading,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    toggleVehicleStatus,
    addInquiry,
    updateInquiryStatus,
    refreshData: fetchData
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};